import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Container, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/system";
import { FaGraduationCap, FaBook, FaPencilAlt } from "react-icons/fa";
import LogoImage from "../assets/images/LLP.png";
import { useThemeContext } from "../contexts/ThemeContext.tsx";

// --- Animációk (Navbar stílusú háttérmozgás) ---
const keyframes = `
@keyframes backgroundMove {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes fade {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
`;

const LoadingWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isClosing" && prop !== "darkMode"
})<{ isClosing: boolean; darkMode: boolean }>(({ darkMode, isClosing }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  // A Navbar-nál látott gradiens logika
  background: darkMode 
    ? 'linear-gradient(45deg, #000000 10%, #5e0b0b 50%, #000000 90%)' 
    : 'linear-gradient(45deg, #25379c 30%, #21cbf3 90%)',
  backgroundSize: '200% 200%',
  animation: 'backgroundMove 12s ease infinite',
  position: "fixed",
  width: "100vw",
  height: "100vh",
  zIndex: 3000, // Legyen minden felett
  overflow: "hidden",
  top: 0,
  left: 0,
  transition: "clip-path 0.8s cubic-bezier(0.77, 0, 0.175, 1), opacity 0.5s ease-in-out",
  clipPath: isClosing ? "circle(0% at 50% 50%)" : "circle(150% at 50% 50%)",
  opacity: isClosing ? 0 : 1,
  pointerEvents: isClosing ? "none" : "auto",
  color: "white"
}));

const FloatingIcon = styled(Box)(() => ({
  position: "absolute",
  animation: "float 6s ease-in-out infinite",
  opacity: 0.15,
  color: "white",
  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-25px)" }
  }
}));

const ProgressWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(4)
}));

const quotes = [
  "Education is the passport to the future.",
  "Learning is a treasure that will follow its owner everywhere.",
  "Knowledge is power.",
  "The beautiful thing about learning is that no one can take it away from you.",
  "Education is not preparation for life; education is life itself.",
  "The expert in anything was once a beginner."
];

const LoadingPage = () => {
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [quote, setQuote] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  // Bezárási időzítő
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsClosing(true);
    }, 1800); 
    return () => clearTimeout(timeout);
  }, []);

  // Idézet kezelés
  useEffect(() => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setQuote(randomQuote);

    const quoteTimer = setInterval(() => {
      const newQuote = quotes[Math.floor(Math.random() * quotes.length)];
      setQuote(newQuote);
    }, 4000);

    return () => clearInterval(quoteTimer);
  }, []);

  return (
    <LoadingWrapper isClosing={isClosing} darkMode={darkMode}>
      <style>{keyframes}</style>
      
      {/* Lebegő ikonok a háttérben */}
      <FloatingIcon sx={{ top: "15%", left: "12%" }}>
        <FaBook size={isMobile ? 35 : 55} />
      </FloatingIcon>
      <FloatingIcon sx={{ top: "25%", right: "18%" }}>
        <FaPencilAlt size={isMobile ? 30 : 45} />
      </FloatingIcon>
      <FloatingIcon sx={{ bottom: "20%", left: "22%" }}>
        <FaGraduationCap size={isMobile ? 40 : 65} />
      </FloatingIcon>

      <Container maxWidth="sm">
        <ProgressWrapper>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="indeterminate"
              size={isMobile ? 110 : 140}
              thickness={2}
              sx={{
                color: "white",
                animationDuration: "1.5s",
                // Navbar stílusú ragyogás
                filter: darkMode ? "drop-shadow(0 0 15px #ff4081)" : "drop-shadow(0 0 15px #FFD700)"
              }}
            />

            <Box
              sx={{
                top: 0, left: 0, bottom: 0, right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Box
                component="img"
                src={LogoImage}
                alt="Logo"
                sx={{
                  width: isMobile ? 50 : 70,
                  height: "auto",
                  filter: "brightness(1.5) drop-shadow(0 0 5px rgba(255,255,255,0.5))"
                }}
              />
            </Box>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                color: "white",
                fontWeight: "bold",
                letterSpacing: "2px",
                textTransform: "uppercase",
                animation: "fade 2s ease-in-out infinite",
                mb: 1
              }}
            >
              Loading Knowledge
            </Typography>
            <Box 
              sx={{ 
                width: "60px", 
                height: "3px", 
                backgroundColor: darkMode ? "#ff4081" : "#FFD700", 
                margin: "0 auto 24px auto",
                borderRadius: "2px"
              }} 
            />
          </Box>

          <Typography
            variant="body1"
            sx={{
              color: "white",
              textAlign: "center",
              fontStyle: "italic",
              maxWidth: "90%",
              margin: "0 auto",
              opacity: 0.9,
              fontSize: isMobile ? "0.95rem" : "1.1rem",
              lineHeight: 1.6
            }}
          >
            "{quote}"
          </Typography>
        </ProgressWrapper>
      </Container>
    </LoadingWrapper>
  );
};

export default LoadingPage;